import React, { useState, useEffect, useCallback } from "react";
import "../StuStyles/StudentDashboardHome.css";
import {
  FaBriefcase,
  FaChartBar,
  FaChalkboardTeacher,
  FaUsers,
  FaArrowRight,
  FaSearch,
  FaFolderOpen,
  FaGraduationCap,
  FaCalendarAlt,
  FaHandshake,
  FaBell,
  FaEnvelope,
  FaHeadset,
  FaLaptop,
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import ApiService from "../../../nhrc_apis/ApiService";

const { JobAPI, TrainingAPI, EventAPI, JobFairAPI } = ApiService;

function StudentHome() {
  const navigate = useNavigate();

  const [activeCard, setActiveCard] = useState(1);
  const [studentName, setStudentName] = useState("Student");

  const [jobCount, setJobCount] = useState(0);
  const [trainCount, setTrainCount] = useState(0);
  const [eventCount, setEventCount] = useState(0);
  const [jobFairCount, setJobFairCount] = useState(0);

  const [pendingCount, setPendingCount] = useState(0);
  const [shortlistedCount, setShortlistedCount] = useState(0);
  const [rejectedCount, setRejectedCount] = useState(0);
  const [totalAppliedCount, setTotalAppliedCount] = useState(0);

  const [loading, setLoading] = useState(false);

  useEffect(() => {
    try {
      const storedUser = localStorage.getItem("loggedInUser");
      if (storedUser) {
        const parsedUser = JSON.parse(storedUser);
        const fullName =
          parsedUser?.name ||
          parsedUser?.full_name ||
          parsedUser?.email ||
          "Student";
        setStudentName(fullName);
      }
    } catch (error) {
      console.log("USER PARSE ERROR:", error);
    }
  }, []);

  const extractArray = (data, keys = []) => {
    if (Array.isArray(data)) return data;
    for (const key of keys) {
      if (Array.isArray(data?.[key])) return data[key];
    }
    if (data && typeof data === "object") {
      const firstArray = Object.values(data).find((v) => Array.isArray(v));
      if (firstArray) return firstArray;
    }
    return [];
  };

  /* =========================================================
     JOB COUNT — now matches Jobs.jsx EXACTLY (status filter only,
     no deadline filtering, since Jobs.jsx never filters by deadline)
  ========================================================= */
  const loadJobCount = useCallback(async () => {
    try {
      const response = await JobAPI.getAllJobs();

      const jobs = extractArray(response?.data, [
        "jobs",
        "data",
        "results",
        "job_list",
        "jobList",
        "items",
      ]);

      if (jobs.length === 0) {
        setJobCount(0);
        return;
      }

      const approvedJobs = jobs.filter((job) => {
        const status = (
          job?.status ||
          job?.approval_status ||
          job?.job_status ||
          ""
        )
          .toString()
          .toUpperCase()
          .trim();

        return (
          status === "APPROVED" ||
          status === "ACTIVE" ||
          status === "OPEN" ||
          status === ""
        );
      });

      setJobCount(approvedJobs.length);
    } catch (err) {
      console.log("LOAD JOBS ERROR :", err?.response?.data || err);
      setJobCount(0);
    }
  }, []);

  const loadTrainingCount = useCallback(async () => {
    try {
      const response = await TrainingAPI.getStudentTrainings();
      const trainings = extractArray(response?.data, [
        "trainings",
        "training_programs",
        "data",
        "results",
        "items",
      ]);
      setTrainCount(trainings.length);
    } catch (err) {
      console.log("LOAD TRAININGS ERROR :", err?.response?.data || err);
      setTrainCount(0);
    }
  }, []);

  const loadEventCount = useCallback(async () => {
    try {
      const response = await EventAPI.getStudentEvents();
      const events = extractArray(response?.data, [
        "events",
        "data",
        "results",
        "items",
      ]);
      setEventCount(events.length);
    } catch (err) {
      console.log("LOAD EVENTS ERROR :", err?.response?.data || err);
      setEventCount(0);
    }
  }, []);

  const loadJobFairCount = useCallback(async () => {
    try {
      const response = await JobFairAPI.getStudentJobFairs();
      const jobFairs = extractArray(response?.data, [
        "job_fairs",
        "jobFairs",
        "data",
        "results",
        "items",
      ]);
      setJobFairCount(jobFairs.length);
    } catch (err) {
      console.log("LOAD JOB FAIRS ERROR :", err?.response?.data || err);
      setJobFairCount(0);
    }
  }, []);

  const loadApplicationCounts = useCallback(async () => {
    try {
      const response = await JobAPI.getStudentAppliedJobs();
      const applications = extractArray(response?.data, [
        "applications",
        "data",
        "results",
        "items",
      ]);

      let pending = 0;
      let shortlisted = 0;
      let rejected = 0;

      applications.forEach((app) => {
        const status = (app?.status || "").toLowerCase();
        if (status === "shortlisted") shortlisted++;
        else if (status === "rejected") rejected++;
        else pending++;
      });

      setTotalAppliedCount(applications.length);
      setPendingCount(pending);
      setShortlistedCount(shortlisted);
      setRejectedCount(rejected);
    } catch (err) {
      console.log("LOAD APPLICATION COUNTS ERROR :", err?.response?.data || err);
      setTotalAppliedCount(0);
      setPendingCount(0);
      setShortlistedCount(0);
      setRejectedCount(0);
    }
  }, []);

  useEffect(() => {
    const loadAll = async () => {
      setLoading(true);
      await Promise.all([
        loadJobCount(),
        loadTrainingCount(),
        loadEventCount(),
        loadJobFairCount(),
        loadApplicationCounts(),
      ]);
      setLoading(false);
    };

    loadAll();
  }, [
    loadJobCount,
    loadTrainingCount,
    loadEventCount,
    loadJobFairCount,
    loadApplicationCounts,
  ]);

  const stuQuickAccess = [
    { icon: <FaSearch />, label: "Browse Jobs", sub: "Find and apply to jobs", color: "green", path: "/student/Jobs" },
    { icon: <FaFolderOpen />, label: "My Applications", sub: "Track your applications", color: "blue", path: "/student/JobStrategy" },
    { icon: <FaGraduationCap />, label: "Training Programs", sub: "Enroll and enhance your skills", color: "purple", path: "/student/TrainingPrograms" },
    { icon: <FaCalendarAlt />, label: "Events", sub: "Register and participate", color: "red", path: "/student/LiveEvents" },
    { icon: <FaHandshake />, label: "Job Fairs", sub: "Attend job fairs and meet employers", color: "orange", path: "/student/Jobfairs" },
  ];

  const stuUpcomingEvents = [
    { date: "25", month: "MAY", title: "Webinar on Career Growth", sub: "Online Event" },
    { date: "30", month: "MAY", title: "Resume Building Workshop", sub: "Online Event" },
    { date: "05", month: "JUN", title: "Mock Interview Session", sub: "Online Event" },
  ];

  return (
    <div className="stu-home-container">

      <div className="stu-welcome-banner d-flex justify-content-between align-items-center">
        <div>
          <h4 className="stu-welcome-title">Welcome back, {studentName}</h4>
          <p className="stu-welcome-text">
            Explore jobs, training programs, events, and job fairs to accelerate
            your career growth through NHRC Student Portal.
          </p>
        </div>
        <div className="stu-banner-illustration d-none d-md-block">
          <FaLaptop className="stu-illustration-icon" />
        </div>
      </div>

      <div className="stu-row g-3">

        <div className="stu-col-half">
          <div
            className={`stu-stat-card ${activeCard === 1 ? "stu-selected-card" : ""}`}
            onClick={() => {
              setActiveCard(1);
              navigate("/student/Jobs");
            }}
          >
            <div className="stu-icon-circle stu-icon-green">
              <FaBriefcase />
            </div>
            <h6 className="stu-card-title">Jobs</h6>
            <h3 className="stu-card-count stu-count-green">{loading ? "-" : jobCount}</h3>
            <span className="stu-card-sub">Active</span>
            <button className="stu-btn-soft stu-btn-green">
              View Jobs <FaArrowRight />
            </button>
          </div>
        </div>

        <div className="stu-col-half">
          <div
            className={`stu-stat-card ${activeCard === 3 ? "stu-selected-card" : ""}`}
            onClick={() => {
              setActiveCard(3);
              navigate("/student/JobStrategy");
            }}
          >
            <div className="stu-icon-circle stu-icon-purple">
              <FaChartBar />
            </div>
            <h6 className="stu-card-title">Application Tracker</h6>
            <h3 className="stu-card-count stu-count-purple" style={{ margin: "4px 0" }}>
              {loading ? "-" : totalAppliedCount}
            </h3>
            <span className="stu-card-sub" style={{ marginBottom: "6px", display: "inline-block" }}>
              Total Applied
            </span>
            <div className="stu-mini-stat-row">
              <div className="stu-mini-stat stu-mini-green">
                <div className="stu-mini-value">{loading ? "-" : shortlistedCount}</div>
                <div className="stu-mini-label">Shortlisted</div>
              </div>
              <div className="stu-mini-stat stu-mini-red">
                <div className="stu-mini-value">{loading ? "-" : rejectedCount}</div>
                <div className="stu-mini-label">Rejected</div>
              </div>
              <div className="stu-mini-stat stu-mini-yellow">
                <div className="stu-mini-value">{loading ? "-" : pendingCount}</div>
                <div className="stu-mini-label">Pending</div>
              </div>
            </div>
            <button className="stu-btn-soft stu-btn-purple">
              View Tracker <FaArrowRight />
            </button>
          </div>
        </div>
      </div>

      <div className="stu-row g-3">

        <div className="stu-col-half">
          <div
            className={`stu-stat-card ${activeCard === 2 ? "stu-selected-card" : ""}`}
            onClick={() => {
              setActiveCard(2);
              navigate("/student/TrainingPrograms");
            }}
          >
            <div className="stu-icon-circle stu-icon-blue">
              <FaChalkboardTeacher />
            </div>
            <h6 className="stu-card-title">TR_Prog / Events</h6>
            <div className="stu-split-stat-row">
              <div className="stu-split-stat">
                <div className="stu-split-value stu-count-blue">{loading ? "-" : trainCount}</div>
                <div className="stu-mini-label">TR Prog</div>
              </div>
              <div className="stu-vr"></div>
              <div className="stu-split-stat">
                <div className="stu-split-value stu-count-blue">{loading ? "-" : eventCount}</div>
                <div className="stu-mini-label">Events</div>
              </div>
            </div>
            <button className="stu-btn-soft stu-btn-blue">
              View All <FaArrowRight />
            </button>
          </div>
        </div>

        <div className="stu-col-half">
          <div
            className={`stu-stat-card ${activeCard === 4 ? "stu-selected-card" : ""}`}
            onClick={() => {
              setActiveCard(4);
              navigate("/student/Jobfairs");
            }}
          >
            <div className="stu-icon-circle stu-icon-orange">
              <FaUsers />
            </div>
            <h6 className="stu-card-title">Job Fairs</h6>
            <h3 className="stu-card-count stu-count-orange">{loading ? "-" : jobFairCount}</h3>
            <span className="stu-card-sub">Active</span>
            <button className="stu-btn-soft stu-btn-orange">
              View Job Fairs <FaArrowRight />
            </button>
          </div>
        </div>
      </div>

      <div className="stu-row g-3">

        <div className="stu-col-wide">
          <div className="stu-stat-card stu-panel-card">
            <h6 className="stu-panel-title">Quick Access</h6>
            <div className="stu-quick-wrap">
              {stuQuickAccess.map((item, idx) => (
                <div
                  key={idx}
                  className="stu-quick-item"
                  onClick={() => navigate(item.path)}
                >
                  <div className={`stu-quick-icon stu-quick-${item.color}`}>
                    {item.icon}
                  </div>
                  <div className="stu-quick-label">{item.label}</div>
                  <div className="stu-quick-sub">{item.sub}</div>
                </div>
              ))}
            </div>

            <div className="stu-panel-links">
              <button className="stu-outline-btn">
                <FaBell className="me-1" /> Manage Alerts
              </button>
              <button className="stu-outline-btn">
                <FaEnvelope className="me-1" /> Newsletter{" "}
                <span className="stu-link-highlight">Subscribe</span>
              </button>
              <button className="stu-outline-btn">
                <FaHeadset className="me-1" /> Need Help?
              </button>
            </div>
          </div>
        </div>

        <div className="stu-col-narrow">
          <div className="stu-stat-card stu-panel-card">
            <div className="d-flex justify-content-between align-items-center stu-panel-header">
              <h6 className="stu-panel-title mb-0">Upcoming Events</h6>
              <a href="#" className="stu-view-all">
                View All
              </a>
            </div>
            {stuUpcomingEvents.map((event, idx) => (
              <div key={idx} className="stu-event-item">
                <div className="d-flex align-items-center">
                  <div className="stu-event-date">
                    <div className="stu-event-month">{event.month}</div>
                    <div className="stu-event-day">{event.date}</div>
                  </div>
                  <div>
                    <div className="stu-event-title">{event.title}</div>
                    <div className="stu-event-sub">{event.sub}</div>
                  </div>
                </div>
                <button className="stu-btn-soft stu-btn-green stu-register-btn">
                  Register Now
                </button>
              </div>
            ))}
            <a href="#" className="stu-contact-link">
              Contact Us
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}

export default StudentHome;